/* eslint-disable max-lines-per-function */
import {
   ButtonVariant,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import {
   PrintsForm,
   PrintsPatientSendEventInterceptor,
   PrintVisitDocumentsActionEventInterceptor,
} from '~libs.desktop.shared.visit-medical-documents.print-from-visit';
import {
   MedicalDocumentationPasswordDetailsShowModalInterceptor,
   MedicalDocumentationShareForm,
} from '~libs.desktop.shared.medical-documentation';

import { visitHistoryTranslationKeys } from '../../../translations';

type PrintOrShareVisitDocumentsShowModalEventInterceptorProps = {
   readonly children: JSX.Element;
   readonly visitId: number;
   readonly patientId: number;
};

export const PrintOrShareVisitDocumentsShowModalEventInterceptor = (
   props: PrintOrShareVisitDocumentsShowModalEventInterceptorProps
): JSX.Element => {
   return (
      <ShowModalEventInterceptor
         testId={'printOrShareVisitDocuments'}
         title={visitHistoryTranslationKeys.printFromVisit}
         // eslint-disable-next-line
         content={({ resolve, reject }): JSX.Element => (
            <PrintsForm>
               {({ trigger, prints }) => (
                  <>
                     <ValidateFormEventInterceptor trigger={trigger}>
                        <PrintVisitDocumentsActionEventInterceptor prints={prints} visitId={props.visitId}>
                           <ModalConfirmButton label={visitHistoryTranslationKeys.print} onClick={resolve} />
                        </PrintVisitDocumentsActionEventInterceptor>
                     </ValidateFormEventInterceptor>

                     {protectFeature(
                        'sendingDocumentsToPatient',
                        <ValidateFormEventInterceptor trigger={trigger}>
                           <ShowModalEventInterceptor
                              testId={'sharePatientDocumentation'}
                              title={visitHistoryTranslationKeys.shareRecordsWithPatient}
                              content={({ resolve, reject }) => (
                                 <MedicalDocumentationShareForm patientId={props.patientId}>
                                    {({ trigger, handleSubmit: handleMedicalDocumentationShareSubmit }) => {
                                       return (
                                          <>
                                             <ValidateFormEventInterceptor trigger={trigger}>
                                                <PrintsPatientSendEventInterceptor
                                                   visitId={props.visitId}
                                                   prints={prints}
                                                   onMedicalDocumentationShareFormSubmit={
                                                      handleMedicalDocumentationShareSubmit
                                                   }
                                                >
                                                   {({ filePassword }) => (
                                                      <MedicalDocumentationPasswordDetailsShowModalInterceptor
                                                         filePassword={filePassword}
                                                         title={
                                                            visitHistoryTranslationKeys.createdAndSentPatientRecords
                                                         }
                                                         successMessage={
                                                            visitHistoryTranslationKeys.sentDocumentationToPatientAndSmsMessageWithPassword
                                                         }
                                                      >
                                                         <ModalConfirmButton
                                                            label={visitHistoryTranslationKeys.send}
                                                            onClick={resolve}
                                                         />
                                                      </MedicalDocumentationPasswordDetailsShowModalInterceptor>
                                                   )}
                                                </PrintsPatientSendEventInterceptor>
                                             </ValidateFormEventInterceptor>

                                             <ModalCancelButton
                                                label={visitHistoryTranslationKeys.back}
                                                onClick={reject}
                                             />
                                             <ModalCloseButton onClick={reject} />
                                          </>
                                       );
                                    }}
                                 </MedicalDocumentationShareForm>
                              )}
                              modalWidth={ModalWidth.NORMAL}
                              shouldShowModal
                              footerItemsHidden
                           >
                              <ModalConfirmButton
                                 testId={'share'}
                                 label={visitHistoryTranslationKeys.share}
                                 variant={ButtonVariant.SECONDARY}
                                 onClick={resolve}
                                 disabled={prints.otherDocuments}
                                 tooltipContent={
                                    prints.otherDocuments
                                       ? visitHistoryTranslationKeys.shareNotAvailableForOtherDocuments
                                       : undefined
                                 }
                              />
                           </ShowModalEventInterceptor>
                        </ValidateFormEventInterceptor>,
                        null
                     )}

                     <ModalCancelButton onClick={reject} />
                     <ModalCloseButton onClick={reject} />
                  </>
               )}
            </PrintsForm>
         )}
         modalWidth={ModalWidth.NORMAL}
         shouldShowModal
         footerItemsHidden
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
